/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const DEACTIVATE_AGENT_DISCRIMINATOR = new Uint8Array([
  205, 171, 239, 225, 82, 126, 96, 166,
]);

export function getDeactivateAgentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    DEACTIVATE_AGENT_DISCRIMINATOR,
  );
}

export type DeactivateAgentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountSigner extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountSigner extends string
        ? WritableSignerAccount<TAccountSigner> &
            AccountSignerMeta<TAccountSigner>
        : TAccountSigner,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type DeactivateAgentInstructionData = {
  discriminator: ReadonlyUint8Array;
  agentId: string;
};

export type DeactivateAgentInstructionDataArgs = { agentId: string };

export function getDeactivateAgentInstructionDataEncoder(): Encoder<DeactivateAgentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["agentId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
    ]),
    (value) => ({ ...value, discriminator: DEACTIVATE_AGENT_DISCRIMINATOR }),
  );
}

export function getDeactivateAgentInstructionDataDecoder(): Decoder<DeactivateAgentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["agentId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
  ]);
}

export function getDeactivateAgentInstructionDataCodec(): Codec<
  DeactivateAgentInstructionDataArgs,
  DeactivateAgentInstructionData
> {
  return combineCodec(
    getDeactivateAgentInstructionDataEncoder(),
    getDeactivateAgentInstructionDataDecoder(),
  );
}

export type DeactivateAgentAsyncInput<
  TAccountAgentAccount extends string = string,
  TAccountSigner extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent account with canonical PDA validation */
  agentAccount?: Address<TAccountAgentAccount>;
  /** Enhanced authority verification */
  signer: TransactionSigner<TAccountSigner>;
  /** Clock sysvar for rate limiting */
  clock?: Address<TAccountClock>;
  agentId: DeactivateAgentInstructionDataArgs["agentId"];
};

export async function getDeactivateAgentInstructionAsync<
  TAccountAgentAccount extends string,
  TAccountSigner extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: DeactivateAgentAsyncInput<
    TAccountAgentAccount,
    TAccountSigner,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  DeactivateAgentInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountSigner,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    signer: { value: input.signer ?? null, isWritable: true },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.agentAccount.value) {
    accounts.agentAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([97, 103, 101, 110, 116])),
        getAddressEncoder().encode(expectAddress(accounts.signer.value)),
        getUtf8Encoder().encode(expectSome(args.agentId)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.signer),
      getAccountMeta(accounts.clock),
    ],
    data: getDeactivateAgentInstructionDataEncoder().encode(
      args as DeactivateAgentInstructionDataArgs,
    ),
    programAddress,
  } as DeactivateAgentInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountSigner,
    TAccountClock
  >);
}

export type DeactivateAgentInput<
  TAccountAgentAccount extends string = string,
  TAccountSigner extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent account with canonical PDA validation */
  agentAccount: Address<TAccountAgentAccount>;
  /** Enhanced authority verification */
  signer: TransactionSigner<TAccountSigner>;
  /** Clock sysvar for rate limiting */
  clock?: Address<TAccountClock>;
  agentId: DeactivateAgentInstructionDataArgs["agentId"];
};

export function getDeactivateAgentInstruction<
  TAccountAgentAccount extends string,
  TAccountSigner extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: DeactivateAgentInput<
    TAccountAgentAccount,
    TAccountSigner,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): DeactivateAgentInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountSigner,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    signer: { value: input.signer ?? null, isWritable: true },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.signer),
      getAccountMeta(accounts.clock),
    ],
    data: getDeactivateAgentInstructionDataEncoder().encode(
      args as DeactivateAgentInstructionDataArgs,
    ),
    programAddress,
  } as DeactivateAgentInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountSigner,
    TAccountClock
  >);
}

export type ParsedDeactivateAgentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Agent account with canonical PDA validation */
    agentAccount: TAccountMetas[0];
    /** Enhanced authority verification */
    signer: TAccountMetas[1];
    /** Clock sysvar for rate limiting */
    clock: TAccountMetas[2];
  };
  data: DeactivateAgentInstructionData;
};

export function parseDeactivateAgentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedDeactivateAgentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agentAccount: getNextAccount(),
      signer: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getDeactivateAgentInstructionDataDecoder().decode(instruction.data),
  };
}
